import axios from 'axios';
import { authenticatedAxios } from './axiosConfig';
import {
  SystemPrompt,
  SystemPromptSummary,
  EvaluationTest,
  EvaluationTestSummary,
  EvaluationExecution,
  EvaluationAnalytics,
  CreateEvaluationTestRequest,
  UpdateEvaluationTestRequest,
  // ExecuteTestRequest,
  SubmitScoreRequest,
  ExecutionFilter,
  AIExecutionScore,
  RetryScoreRequest,
  BatchAIScoresRequest,
  AIRetestProgress,
  RetestRunningResponse,
  AIRetestStartResponse,
  ScoringValidationRun,
  ScoringValidationResult,
  ScoringValidationMetrics,
  CreateScoringValidationRunRequest,
} from '../types/evaluation';

/**
 * API client service for evaluation testing system endpoints.
 * Provides methods for system prompts, evaluation tests, executions, and analytics.
 */
export class EvaluationApiService {
  private static instance: EvaluationApiService;

  private constructor() {}

  public static getInstance(): EvaluationApiService {
    if (!EvaluationApiService.instance) {
      EvaluationApiService.instance = new EvaluationApiService();
    }
    return EvaluationApiService.instance;
  }

  // ==================== System Prompt Endpoints ====================

  /**
   * Get a system prompt by its hash.
   * @param hash - The system prompt hash
   * @returns The system prompt or null if not found
   */
  async getSystemPrompt(hash: string): Promise<SystemPrompt | null> {
    try {
      const response = await authenticatedAxios.get<SystemPrompt>(
        `/evaluation/system-prompts/${hash}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get system prompt');
      }
      throw error;
    }
  }

  /**
   * List system prompts with optional inclusion of inactive prompts.
   * @param includeInactive - Whether to include inactive prompts (default: false)
   * @returns Array of system prompt summaries
   */
  async listSystemPrompts(includeInactive: boolean = false): Promise<SystemPromptSummary[]> {
    try {
      const response = await authenticatedAxios.get<SystemPromptSummary[]>(
        '/evaluation/system-prompts',
        { params: { includeInactive } }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list system prompts');
      }
      throw error;
    }
  }

  /**
   * Mark a system prompt as inactive.
   * @param hash - The system prompt hash
   * @returns True if successful
   */
  async markSystemPromptInactive(hash: string): Promise<boolean> {
    try {
      await authenticatedAxios.put(
        `/evaluation/system-prompts/${hash}/inactive`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to mark system prompt as inactive');
      }
      throw error;
    }
  }

  /**
   * Mark a system prompt as active.
   * @param hash - The system prompt hash
   * @returns True if successful
   */
  async markSystemPromptActive(hash: string): Promise<boolean> {
    try {
      await authenticatedAxios.put(
        `/evaluation/system-prompts/${hash}/active`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to mark system prompt as active');
      }
      throw error;
    }
  }

  // ==================== Evaluation Test Endpoints ====================

  /**
   * Create a new evaluation test.
   * @param request - The evaluation test configuration
   * @returns The created test ID
   */
  async createEvaluationTest(request: CreateEvaluationTestRequest): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ id: string }>(
        '/evaluation/tests',
        request
      );
      return response.data.id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to create evaluation test');
      }
      throw error;
    }
  }

  /**
   * Get an evaluation test by ID.
   * @param testId - The test ID
   * @returns The evaluation test or null if not found
   */
  async getEvaluationTest(testId: string): Promise<EvaluationTest | null> {
    try {
      const response = await authenticatedAxios.get<EvaluationTest>(
        `/evaluation/tests/${testId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get evaluation test');
      }
      throw error;
    }
  }

  /**
   * List all evaluation tests with optional inclusion of deleted tests.
   * @param includeDeleted - Whether to include deleted tests (default: false)
   * @param testOriginFilter - Filter by origin: undefined=all, "user"=no source, "ai"=has source
   * @returns Array of evaluation test summaries
   */
  async listEvaluationTests(includeDeleted: boolean = false, testOriginFilter?: string): Promise<EvaluationTestSummary[]> {
    try {
      const params: Record<string, string | boolean> = { includeDeleted };
      if (testOriginFilter) {
        params.testOriginFilter = testOriginFilter;
      }
      const response = await authenticatedAxios.get<EvaluationTestSummary[]>(
        '/evaluation/tests',
        { params }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list evaluation tests');
      }
      throw error;
    }
  }

  /**
   * Update an existing evaluation test (only if execution count == 0).
   * @param testId - The test ID
   * @param request - The updated test configuration
   * @returns True if successful
   */
  async updateEvaluationTest(testId: string, request: UpdateEvaluationTestRequest): Promise<boolean> {
    try {
      await authenticatedAxios.put(
        `/evaluation/tests/${testId}`,
        request
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to update evaluation test');
      }
      throw error;
    }
  }

  /**
   * Clone an existing evaluation test.
   * @param testId - The test ID to clone
   * @returns The cloned test ID
   */
  async cloneEvaluationTest(testId: string): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ id: string }>(
        `/evaluation/tests/${testId}/clone`
      );
      return response.data.id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to clone evaluation test');
      }
      throw error;
    }
  }

  /**
   * Soft-delete an evaluation test (only if execution count == 0).
   * @param testId - The test ID
   * @returns True if successful
   */
  async softDeleteEvaluationTest(testId: string): Promise<boolean> {
    try {
      await authenticatedAxios.delete(
        `/evaluation/tests/${testId}`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete evaluation test');
      }
      throw error;
    }
  }

  /**
   * Force-delete an evaluation test with password (even if it has executions).
   * @param testId - The test ID
   * @param password - The password for force deletion
   * @returns True if successful
   */
  async forceDeleteEvaluationTest(testId: string, password: string): Promise<boolean> {
    try {
      await authenticatedAxios.delete(
        `/evaluation/tests/${testId}/force`,
        { data: { password } }
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to force delete evaluation test');
      }
      throw error;
    }
  }

  // ==================== Execution Endpoints ====================

  /**
   * Execute an evaluation test with a client-provided execution ID.
   * @param executionId - Client-generated execution ID
   * @param testId - The test ID
   * @param prompt - The prompt to execute
   * @returns True if successful
   */
  async executeEvaluation(executionId: string, testId: string, prompt: string): Promise<boolean> {
    try {
      await authenticatedAxios.post(
        `/evaluation/executions/${executionId}/execute`,
        { testId, prompt }
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to execute evaluation');
      }
      throw error;
    }
  }

  /**
   * Get an execution by ID.
   * @param executionId - The execution ID
   * @returns The execution or null if not found
   */
  async getExecution(executionId: string): Promise<EvaluationExecution | null> {
    try {
      const response = await authenticatedAxios.get<EvaluationExecution>(
        `/evaluation/executions/${executionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get execution');
      }
      throw error;
    }
  }

  /**
   * List executions for a test with optional filtering.
   * @param testId - The test ID
   * @param filter - Optional filter parameters
   * @returns Array of executions
   */
  async listExecutions(testId: string, filter?: ExecutionFilter): Promise<EvaluationExecution[]> {
    try {
      const params: Record<string, string> = {};
      if (filter?.userId) params.userId = filter.userId;
      if (filter?.minScore !== undefined) params.minScore = filter.minScore.toString();
      if (filter?.maxScore !== undefined) params.maxScore = filter.maxScore.toString();
      if (filter?.startDate) params.startDate = filter.startDate;
      if (filter?.endDate) params.endDate = filter.endDate;

      const response = await authenticatedAxios.get<EvaluationExecution[]>(
        `/evaluation/tests/${testId}/executions`,
        { params }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list executions');
      }
      throw error;
    }
  }

  /**
   * Submit a score for an execution.
   * @param executionId - The execution ID
   * @param request - The score submission request
   * @returns True if successful
   */
  async submitScore(executionId: string, request: SubmitScoreRequest): Promise<boolean> {
    try {
      await authenticatedAxios.post(
        `/evaluation/executions/${executionId}/score`,
        request
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to submit score');
      }
      throw error;
    }
  }

  /**
   * Export all unique prompts from a test's executions as a JSON array.
   * @param testId - The test ID
   * @returns Array of unique prompt strings
   */
  async exportPrompts(testId: string): Promise<string[]> {
    try {
      const response = await authenticatedAxios.get<string[]>(
        `/evaluation/tests/${testId}/export-prompts`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to export prompts');
      }
      throw error;
    }
  }

  // ==================== Analytics Endpoints ====================

  /**
   * Get analytics for an evaluation test.
   * @param testId - The test ID
   * @returns The analytics data
   */
  async getAnalytics(testId: string): Promise<EvaluationAnalytics> {
    try {
      const response = await authenticatedAxios.get<EvaluationAnalytics>(
        `/evaluation/tests/${testId}/analytics`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get analytics');
      }
      throw error;
    }
  }

  // ==================== Execution Scores Endpoints ====================

  /**
   * Get all scores for an execution with aggregate data.
   * @param executionId - The execution ID
   * @returns The scores aggregate response
   */
  async getExecutionScores(executionId: string): Promise<any> {
    try {
      const response = await authenticatedAxios.get(
        `/execution-scores/${executionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get execution scores');
      }
      throw error;
    }
  }

  // ==================== AI Execution Scores Endpoints ====================

  /**
   * Retry AI scoring for a provider result.
   * @param request - The retry score request
   * @returns The regenerated AI score
   */
  async retryAIScore(request: RetryScoreRequest): Promise<AIExecutionScore> {
    try {
      console.log('Retrying AI score with request:', request);
      console.log('Full URL:', `${authenticatedAxios.defaults.baseURL}/ai-scores/retry`);
      const response = await authenticatedAxios.post<AIExecutionScore>(
        '/ai-scores/retry',
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Retry AI score error:', error.response?.status, error.response?.data);
        throw new Error(error.response?.data?.message || 'Failed to retry AI score');
      }
      throw error;
    }
  }

  /**
   * Get AI score for a specific provider result.
   * @param evaluationExecutionId - The evaluation execution ID
   * @param providerId - The provider ID
   * @returns The AI score or null if not found
   */
  async getAIScore(evaluationExecutionId: string, providerId: string): Promise<AIExecutionScore | null> {
    try {
      const response = await authenticatedAxios.get<AIExecutionScore>(
        `/ai-scores/${evaluationExecutionId}/${providerId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get AI score');
      }
      throw error;
    }
  }

  /**
   * Get all AI scores for an evaluation execution.
   * @param evaluationExecutionId - The evaluation execution ID
   * @returns List of AI scores for all providers
   */
  async getAIScoresForExecution(evaluationExecutionId: string): Promise<AIExecutionScore[]> {
    try {
      const response = await authenticatedAxios.get<AIExecutionScore[]>(
        `/ai-scores/execution/${evaluationExecutionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get AI scores');
      }
      throw error;
    }
  }

  /**
   * Get AI scores for multiple evaluation executions (batch).
   * @param request - The batch request with evaluation execution IDs
   * @returns Dictionary mapping execution ID to list of AI scores
   */
  async getAIScoresBatch(request: BatchAIScoresRequest): Promise<Record<string, AIExecutionScore[]>> {
    try {
      const response = await authenticatedAxios.post<Record<string, AIExecutionScore[]>>(
        '/ai-scores/batch',
        request
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get batch AI scores');
      }
      throw error;
    }
  }

  /**
   * Enqueue AI scoring for all provider results in an evaluation execution.
   * Used for backfilling scores for past executions.
   * @param evaluationExecutionId - The evaluation execution ID
   * @returns Number of scoring requests enqueued
   */
  async enqueueAIScoringForExecution(evaluationExecutionId: string): Promise<{ enqueuedCount: number; message: string }> {
    try {
      const response = await authenticatedAxios.post<{ enqueuedCount: number; message: string }>(
        `/ai-scores/enqueue/execution/${evaluationExecutionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to enqueue AI scoring');
      }
      throw error;
    }
  }

  /**
   * Enqueue AI scoring by provider execution flow ID.
   * Finds the evaluation execution and enqueues scoring for all providers.
   * @param providerExecutionId - The provider execution flow ID
   * @returns Number of scoring requests enqueued and evaluation execution ID
   */
  async enqueueAIScoringByProviderExecutionId(providerExecutionId: string): Promise<{ enqueuedCount: number; message: string; evaluationExecutionId: string }> {
    try {
      const response = await authenticatedAxios.post<{ enqueuedCount: number; message: string; evaluationExecutionId: string }>(
        `/ai-scores/enqueue/provider-execution/${providerExecutionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to enqueue AI scoring');
      }
      throw error;
    }
  }

  /**
   * Enqueue AI scoring for all executions of an evaluation test.
   * Used for backfilling scores for all past executions of a test.
   * @param testId - The evaluation test ID
   * @returns Number of scoring requests enqueued
   */
  async enqueueAIScoringForTest(testId: string): Promise<{ enqueuedCount: number; message: string }> {
    try {
      const response = await authenticatedAxios.post<{ enqueuedCount: number; message: string }>(
        `/ai-scores/enqueue/test/${testId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to enqueue AI scoring');
      }
      throw error;
    }
  }

  /**
   * Re-run AI scoring for an evaluation execution (force re-score).
   * Deletes existing scores and enqueues new scoring requests.
   * @param evaluationExecutionId - The evaluation execution ID
   * @returns Number of scoring requests enqueued
   */
  async rerunAIScoringForExecution(evaluationExecutionId: string): Promise<{ enqueuedCount: number; message: string }> {
    try {
      const response = await authenticatedAxios.post<{ enqueuedCount: number; message: string }>(
        `/ai-scores/rerun/execution/${evaluationExecutionId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to re-run AI scoring');
      }
      throw error;
    }
  }

  /**
   * Re-run AI scoring for all executions of an evaluation test (force re-score).
   * Deletes existing scores and enqueues new scoring requests.
   * @param testId - The evaluation test ID
   * @returns Number of scoring requests enqueued
   */
  async rerunAIScoringForTest(testId: string): Promise<{ enqueuedCount: number; message: string }> {
    try {
      const response = await authenticatedAxios.post<{ enqueuedCount: number; message: string }>(
        `/ai-scores/rerun/test/${testId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to re-run AI scoring');
      }
      throw error;
    }
  }

  // ==================== Retest Progress Endpoints ====================

  /**
   * Start an AI retest for a test with modified configuration.
   * Creates a new test and executes historical prompts in the background.
   * @param testId - The source test ID to retest
   * @param testConfig - The modified test configuration
   * @param customPrompts - Optional custom prompts to use instead of historical prompts
   * @returns Response with new test ID and prompt count
   */
  async startAIRetest(testId: string, testConfig: EvaluationTest, customPrompts?: string[]): Promise<AIRetestStartResponse> {
    try {
      const body = customPrompts ? { testConfig, customPrompts } : { testConfig };
      const response = await authenticatedAxios.post<AIRetestStartResponse>(
        `/evaluation/tests/${testId}/ai-retest`,
        body
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as { message?: string; runningTestId?: string };
        throw new Error(data?.message || 'Another AI evaluation is currently running');
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to start AI retest');
      }
      throw error;
    }
  }

  /**
   * Start a batch run with custom prompts from a JSON file.
   * Creates a new test and executes all prompts in the background.
   * @param testConfig - The evaluation test configuration
   * @param prompts - Array of prompt strings
   * @returns Response with new test ID and prompt count
   */
  async startBatchRun(testConfig: EvaluationTest, prompts: string[]): Promise<{ testId: string; promptCount: number }> {
    try {
      const response = await authenticatedAxios.post<{ testId: string; promptCount: number }>(
        '/evaluation/tests/batch-run',
        { testConfig, prompts }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        const data = error.response.data as { message?: string; runningTestId?: string };
        throw new Error(data?.message || 'Another AI evaluation is currently running');
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to start batch run');
      }
      throw error;
    }
  }

  /**
   * Cancel a running AI retest/batch evaluation.
   * @param testId - The test ID to cancel
   * @returns True if cancelled successfully
   */
  async cancelRetest(testId: string): Promise<boolean> {
    try {
      await authenticatedAxios.post(
        `/evaluation/tests/${testId}/cancel-retest`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to cancel evaluation');
      }
      throw error;
    }
  }

  /**
   * Get retest progress for a test.
   * @param testId - The test ID
   * @returns The retest progress or null if not found
   */
  async getRetestProgress(testId: string): Promise<AIRetestProgress | null> {
    try {
      const response = await authenticatedAxios.get<AIRetestProgress>(
        `/evaluation/tests/${testId}/retest-progress`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get retest progress');
      }
      throw error;
    }
  }

  /**
   * Check if any AI retest is currently running.
   * @returns Object with isRunning flag and optional testId
   */
  async isRetestRunning(): Promise<RetestRunningResponse> {
    try {
      const response = await authenticatedAxios.get<RetestRunningResponse>(
        '/evaluation/retest-running'
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to check retest status');
      }
      throw error;
    }
  }

  // ==================== Scoring Validation Endpoints ====================

  /**
   * Ingest a new scoring system prompt.
   * @param content - The system prompt content
   * @param description - Human-readable description
   * @param purpose - Detailed explanation of usage
   * @returns The hash of the ingested prompt
   */
  async ingestScoringPrompt(content: string, description: string, purpose: string): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ hash: string }>(
        '/scoring-validation/system-prompts',
        { content, description, purpose }
      );
      return response.data.hash;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to ingest scoring prompt');
      }
      throw error;
    }
  }

  /**
   * Create a new scoring validation run.
   * @param request - The validation run configuration
   * @returns The created run ID
   */
  async createScoringValidationRun(request: CreateScoringValidationRunRequest): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ id: string }>(
        '/scoring-validation/runs',
        request
      );
      return response.data.id;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to create scoring validation run');
      }
      throw error;
    }
  }

  /**
   * Start a scoring validation run.
   * @param runId - The run ID to start
   * @returns Confirmation message
   */
  async startScoringValidationRun(runId: string): Promise<string> {
    try {
      const response = await authenticatedAxios.post<{ message: string }>(
        `/scoring-validation/runs/${runId}/start`
      );
      return response.data.message;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to start scoring validation run');
      }
      throw error;
    }
  }

  /**
   * List scoring validation runs.
   * @param sourceTestId - Optional source test ID filter
   * @returns Array of validation runs
   */
  async listScoringValidationRuns(sourceTestId?: string): Promise<ScoringValidationRun[]> {
    try {
      const response = await authenticatedAxios.get<ScoringValidationRun[]>(
        '/scoring-validation/runs',
        { params: sourceTestId ? { sourceTestId } : undefined }
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to list scoring validation runs');
      }
      throw error;
    }
  }

  /**
   * Get a scoring validation run by ID.
   * @param runId - The run ID
   * @returns The validation run or null if not found
   */
  async getScoringValidationRun(runId: string): Promise<ScoringValidationRun | null> {
    try {
      const response = await authenticatedAxios.get<ScoringValidationRun>(
        `/scoring-validation/runs/${runId}`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get scoring validation run');
      }
      throw error;
    }
  }

  /**
   * Get results for a scoring validation run.
   * @param runId - The run ID
   * @returns Array of validation results
   */
  async getScoringValidationResults(runId: string): Promise<ScoringValidationResult[]> {
    try {
      const response = await authenticatedAxios.get<ScoringValidationResult[]>(
        `/scoring-validation/runs/${runId}/results`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to get scoring validation results');
      }
      throw error;
    }
  }

  /**
   * Delete a scoring validation run.
   * @param runId - The run ID to delete
   * @returns True if successful
   */
  async deleteScoringValidationRun(runId: string): Promise<boolean> {
    try {
      await authenticatedAxios.delete(
        `/scoring-validation/runs/${runId}`
      );
      return true;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to delete scoring validation run');
      }
      throw error;
    }
  }

  /**
   * Recompute metrics for a completed scoring validation run.
   * @param runId - The run ID
   * @returns The recomputed metrics
   */
  async recomputeScoringValidationMetrics(runId: string): Promise<ScoringValidationMetrics> {
    try {
      const response = await authenticatedAxios.post<ScoringValidationMetrics>(
        `/scoring-validation/runs/${runId}/recompute-metrics`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || 'Failed to recompute metrics');
      }
      throw error;
    }
  }
}
